import { propertyMedia } from "@/lib/property-media";
import { PropertyPicture } from "@/components/property/property-media";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/ui/section-heading";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Waves, 
  Wifi, 
  Car, 
  UtensilsCrossed, 
  Wind, 
  Flame,
  Coffee,
  Tv,
  Dumbbell,
  Bath,
  ShirtIcon,
  Zap,
  Refrigerator,
  Droplets,
  Package,
  Heart,
  Speaker,
  Book,
  Armchair,
  Umbrella,
  Baby,
  Gamepad2,
  Moon,
  Shield,
  Key,
  Battery,
  ChefHat,
  Snowflake,
  Users,
  TreePine,
  MapPin,
  Shirt,
  Utensils,
  Home,
  Bed,
  Sofa,
  Monitor,
  Gamepad,
  Music,
  Camera,
  Sun,
  Star,
  CheckCircle,
  Award,
  Clock,
  Thermometer,
  Mountain,
  Fish,
  Sailboat,
  Dices,
  WashingMachine,
  Microwave,
  CookingPot,
  Box
} from "lucide-react";

interface Amenity {
  id: string;
  name: string;
  icon: string;
  category: string;
  description?: string;
  featured?: boolean;
}

interface AmenityCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order: number;
}

export function AmenitiesGrid({ propertySlug }: { propertySlug?: string } = {}) {
  // Scoped to one home when a slug is given; the legacy default home otherwise.
  const { data: amenities = [], isLoading, error } = useQuery<Amenity[]>({
    queryKey: propertySlug ? ["/api/amenities", { property: propertySlug }] : ["/api/amenities"],
  });

  const { data: categories = [] } = useQuery<AmenityCategory[]>({
    queryKey: ["/api/amenity-categories"],
  });


  const getIcon = (iconName: string, size: 'sm' | 'md' | 'lg' = 'md') => {
    const iconMap: { [key: string]: React.ElementType } = {
      'waves': Waves,
      'wifi': Wifi,
      'car': Car,
      'utensils': UtensilsCrossed,
      'wind': Wind,
      'sparkles': CheckCircle,
      'flame': Flame,
      'coffee': Coffee,
      'tv': Tv,
      'dumbbell': Dumbbell,
      'bath': Bath,
      'shirt': ShirtIcon,
      'zap': Zap,
      'refrigerator': Refrigerator,
      'droplets': Droplets,
      'package': Package,
      'heart': Heart,
      'speaker': Speaker,
      'book': Book,
      'chair': Armchair,
      'umbrella': Umbrella,
      'baby': Baby,
      'gamepad2': Gamepad2,
      'moon': Moon,
      'shield': Shield,
      'key': Key,
      'battery': Battery,
      'chef': ChefHat,
      'snowflake': Snowflake,
      'users': Users,
      'tree': TreePine,
      'map': MapPin,
      'laundry': Shirt,
      'kitchen': Utensils,
      'home': Home,
      'bed': Bed,
      'sofa': Sofa,
      'monitor': Monitor,
      'gaming': Gamepad,
      'music': Music,
      'camera': Camera,
      'sun': Sun,
      'star': Star,
      'check': CheckCircle,
      'award': Award,
      'clock': Clock,
      'thermometer': Thermometer,
      'mountain': Mountain,
      'fish': Fish,
      'kayak': Sailboat,
      'umbrella-beach': Umbrella,
      'fire': Flame,
      'dice': Dices,
      'washing-machine': WashingMachine,
      'tshirt': Shirt,
      'box': Box,
      'couch': Sofa,
      'baby-carriage': Baby,
      'wave-square': Microwave,
      'soap': Droplets,
      'blender': CookingPot,
    };
    
    const sizeClasses = {
      'sm': 'h-4 w-4 stroke-1',
      'md': 'h-6 w-6 stroke-1',
      'lg': 'h-8 w-8 stroke-1'
    };
    
    const IconComponent = iconMap[iconName] || CheckCircle;
    return <IconComponent className={sizeClasses[size]} />;
  };

  // Organize amenities by category
  const amenitiesByCategory = amenities.reduce((acc, amenity) => {
    if (!acc[amenity.category]) acc[amenity.category] = [];
    acc[amenity.category].push(amenity);
    return acc;
  }, {} as Record<string, Amenity[]>);

  // Sort categories by their sort_order from database
  const sortedCategories = categories
    .filter(cat => amenitiesByCategory[cat.name] && amenitiesByCategory[cat.name].length > 0)
    .sort((a, b) => a.sort_order - b.sort_order);

  // Get featured amenities to display on main page (max 6)
  // Shows ONLY amenities marked as featured, limited to 6 for optimal layout
  // If none are marked as featured, fallback to first 6 as a reasonable default
  // All amenities (featured and non-featured) are shown in the "See All" lightbox
  const featuredAmenities = (() => {
    // First try to get amenities marked as featured
    const markedFeatured = amenities.filter(amenity => amenity.featured);
    if (markedFeatured.length > 0) {
      return markedFeatured.slice(0, 6);
    }
    
    // Fallback to first 6 amenities if none are marked as featured
    return amenities.slice(0, 6);
  })();

  // Create dynamic category configuration from database
  const categoryConfig = categories.reduce((acc, category) => {
    acc[category.name] = {
      icon: category.icon || 'check',
      title: category.name,
      description: category.description || `Everything you need for ${category.name.toLowerCase()}`,
      color: category.color || 'from-driftwood to-sand-deep'
    };
    return acc;
  }, {} as Record<string, { icon: string; title: string; description: string; color: string }>);

  // Fallback configuration for categories not yet configured
  const getFallbackCategoryConfig = (categoryName: string) => {
    return {
      icon: 'check',
      title: categoryName,
      description: `Premium ${categoryName.toLowerCase()} amenities for your stay`,
      color: 'from-driftwood to-sand-deep'
    };
  };

  if (isLoading) {
    return (
      <section id="amenities" className="py-16 md:py-24 bg-shell">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 md:mb-20">
              <Skeleton className="h-4 w-32 mx-auto mb-3" />
              <Skeleton className="h-12 w-80 max-w-full mx-auto mb-4" />
              <Skeleton className="h-6 w-96 max-w-full mx-auto" />
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-sand-deep rounded-sm overflow-hidden bg-shell">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={`p-8 md:p-10 ${
                    i % 2 === 0 ? 'md:border-r border-sand-deep' : ''
                  } ${
                    i < 4 ? 'border-b border-sand-deep' : ''
                  }`}>
                    <div className="flex items-start space-x-5">
                      <Skeleton className="w-12 h-12 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-6 w-32" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state when no amenities are available
  if (error || amenities.length === 0) {
    return (
      <section id="amenities" className="py-16 md:py-24 bg-shell border-t border-sand-deep">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              align="center"
              eyebrow="Amenities"
              title="What this place offers"
              lede="Everything you need for the perfect tropical getaway"
              className="mb-16 md:mb-20"
            />
            <div className="max-w-lg mx-auto text-center py-16">
              <div className="border border-sand-deep rounded-sm p-12 bg-shell">
                <div className="text-ink-soft mb-6 font-light">
                  {error ? "Unable to load amenities at this time." : "No amenities configured yet."}
                </div>
                {error && (
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline"
                    className="border-sand-deep text-ink hover:bg-sand hover:text-ink font-sans tracking-wide"
                    data-testid="retry-amenities"
                  >
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="amenities" className="py-20 md:py-24 bg-shell border-t border-sand-deep section-stable">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="amenities-editorial grid lg:grid-cols-[0.9fr_1.1fr] border border-sand-deep bg-shell">
            <div className="min-w-0">
              {propertyMedia(propertySlug || 'the-kualoa-beach-house') && <PropertyPicture
                media={{...propertyMedia(propertySlug || 'the-kualoa-beach-house')!, desktopImage: `/media/property/${propertySlug === 'caldwell-beach-house' ? 'caldwell' : 'kualoa'}-desktop.webp`, mobileImage: `/media/property/${propertySlug === 'caldwell-beach-house' ? 'caldwell' : 'kualoa'}-mobile.webp`}}
                alt={propertySlug === 'caldwell-beach-house' ? 'Caldwell house and its oceanfront setting' : 'Kualoa house with its beachfront garden and mountain backdrop'}
                className="w-full aspect-[4/3] object-cover" />}
              <div className="p-6 md:p-9">
                <SectionHeading align="left" eyebrow="Make yourself at home" title="Beach House Accommodations" className="mb-4" />
                <a href="#photos" className="inline-flex min-h-11 items-center text-sm text-forest underline underline-offset-4">Explore the house in photos →</a>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 border-t lg:border-t-0 lg:border-l border-sand-deep">
              {featuredAmenities.map((amenity, index) => {
                const isLeft = index % 2 === 0;
                
                return (
                  <div 
                    key={amenity.id}
                    className={`p-5 md:p-6 border-b border-sand-deep
                      ${isLeft ? 'md:border-r border-sand-deep' : ''}
                    `}
                    data-testid={`amenity-${amenity.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {/* Simple top accent line */}
                    <div className="w-7 h-[2px] bg-palm/50 mb-4" />
                    
                    <h3 className="text-lg md:text-xl font-serif font-normal text-forest mb-3 leading-tight tracking-luxury-tight flex items-center gap-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-palm/10 text-palm">{getIcon(amenity.icon, 'sm')}</span>
                      {amenity.name}
                    </h3>
                    <p className="text-luxury-base font-sans text-ink-soft leading-relaxed font-light">
                      {amenity.description || amenity.name}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Refined view all button */}
          <div className="text-center mt-8">
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  size="lg"
                  className="bg-transparent border border-ink text-ink hover:bg-ink hover:text-shell font-sans font-medium tracking-wide shadow-none"
                  data-testid="show-all-amenities"
                >
                  View Complete Amenities ({amenities.length})
                </Button>
              </DialogTrigger>
              
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-shell border border-sand-deep rounded-sm p-0">
                <DialogHeader className="relative overflow-hidden border-b border-sand-deep pt-12 pb-10 px-6">
                  <div className="relative z-10 text-center">
                    <DialogTitle className="text-luxury-2xl md:text-luxury-3xl font-serif text-forest mb-4 leading-tight tracking-luxury-tight">
                        Complete Amenities Collection
                    </DialogTitle>
                    <div className="w-16 h-px bg-shell/40 mx-auto mb-4" />
                    <p className="text-ink-soft text-luxury-base font-light max-w-2xl mx-auto leading-relaxed">
                      Amenities available at this home
                    </p>
                  </div>
                </DialogHeader>
                
                <div className="overflow-y-auto max-h-[70vh] px-8 bg-shell">
                  <div className="space-y-12 py-10">
                      {sortedCategories.map((category) => {
                        const categoryAmenities = amenitiesByCategory[category.name];
                        const config = categoryConfig[category.name] || getFallbackCategoryConfig(category.name);
                        
                        return (
                          <div 
                            key={category.id}
                            className="bg-shell border border-sand-deep rounded p-8 md:p-10"
                          >
                            {/* Category header */}
                            <div className="mb-8">
                              <h3 className="text-luxury-2xl font-serif font-normal text-forest mb-2.5 leading-tight tracking-luxury-tight">{config.title}</h3>
                              <p className="text-ink-soft text-luxury-base font-light leading-relaxed">{config.description}</p>
                            </div>
                            
                            {/* Amenities grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                              {categoryAmenities.map((amenity, index) => (
                                <div 
                                  key={amenity.id}
                                  className="group p-5 hover:bg-sand transition-colors cursor-pointer rounded border border-transparent hover:border-sand-deep"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${amenity.name}: ${amenity.description}`}
                                >
                                  <div>
                                    <div className="flex items-start justify-between mb-3">
                                      <h4 className="font-serif font-normal text-forest text-luxury-lg leading-tight tracking-tight flex items-center gap-2">
                                        <span className="text-driftwood group-hover:text-ink transition-colors">{getIcon(amenity.icon, 'sm')}</span>
                                        {amenity.name}
                                      </h4>
                                      {amenity.featured && (
                                        <span className="text-[11px] text-driftwood font-semibold uppercase tracking-[0.3em] ml-3 flex-shrink-0">
                                          Featured
                                        </span>
                                      )}
                                    </div>
                                    {amenity.description && (
                                      <p className="text-ink-soft leading-relaxed text-luxury-sm font-light">
                                        {amenity.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
}
